import { Box, MessageBlock } from '@deckee/deck-hand';
import React, {
  FC,
  MouseEvent,
  MutableRefObject,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  type KeyboardEvent,
} from 'react';
import styled, { useTheme } from 'styled-components';
import DragScroller from '../../../../shared/_deprecated/components/DragScroller/DragScroller';
import Shimmer from '../../../../shared/Shimmer/Shimmer';
import {
  getWeatherPrecisIcon,
  ReactSvg,
} from '../../../../shared/vector-images';
import { captureEvent } from '../../../../../services/event-capture';
import { type WeatherForecastDaySelectHandler } from '../../helpers/analytics/weatherForecastAnalytics';
import {
  EXTENDED_FORECAST_CTA_DAY,
  FREE_FORECAST_DAYS,
} from '../../helpers/forecastHorizon';
import { type WeatherForecastDay } from '../../helpers/outlook/formatDays';
import { type WeatherForecastRiskLevel } from '../../helpers/risk/riskLevels';
import { type WeatherForecastOutlookError } from '../../hooks/useWeatherForecastOutlook';
import { scrollDayStripToSelectedDay } from '../../helpers/day/scrollDayStrip';
import ExtendedForecastCtaChip from '../ExtendedForecastCta/ExtendedForecastCtaChip';

const ScrollContainer = styled(Box)<{ $edgeInset: number }>`
  display: flex;
  gap: 8px;
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  margin-left: -${({ $edgeInset }) => $edgeInset}px;
  margin-right: -${({ $edgeInset }) => $edgeInset}px;
  padding-left: ${({ $edgeInset, theme }) => $edgeInset + theme.space[2]}px;
  padding-right: ${({ $edgeInset, theme }) => $edgeInset + theme.space[2]}px;
  cursor: grab;
  user-select: none;

  &:active {
    cursor: grabbing;
  }

  &::-webkit-scrollbar {
    display: none;
  }
`;

const MIN_SCROLL_DISTANCE_PX = 5;

const DragScrollStrip: FC<{
  children: (helpers: {
    clickItem: (callback: () => void) => void;
  }) => ReactNode;
  edgeInset: number;
  scrollContainerRef?: MutableRefObject<HTMLElement | null>;
  'data-testid'?: string;
  'aria-busy'?: boolean;
  onStopScrolling?: () => void;
}> = ({
  children,
  edgeInset,
  scrollContainerRef,
  onStopScrolling,
  ...containerProps
}) => {
  const scrollStartRef = useRef(0);
  const scrollElementRef = useRef<HTMLElement | null>(null);

  const handleStopScrolling = useCallback(() => {
    if (!scrollElementRef.current || !onStopScrolling) return;
    const delta = Math.abs(
      scrollElementRef.current.scrollLeft - scrollStartRef.current,
    );
    if (delta >= MIN_SCROLL_DISTANCE_PX) {
      onStopScrolling();
    }
  }, [onStopScrolling]);

  return (
    <DragScroller onStopScrolling={handleStopScrolling}>
      {({
        onMouseDown,
        ref,
        clickItem,
      }: {
        onMouseDown: (event: MouseEvent<HTMLElement>) => void;
        ref: (element: HTMLElement | null) => void;
        clickItem: (callback: () => void) => void;
      }) => (
        <ScrollContainer
          ref={(element: HTMLElement | null) => {
            scrollElementRef.current = element;
            if (scrollContainerRef) {
              scrollContainerRef.current = element;
            }
            ref(element);
          }}
          onMouseDown={(event: MouseEvent<HTMLElement>) => {
            if (scrollElementRef.current) {
              scrollStartRef.current = scrollElementRef.current.scrollLeft;
            }
            onMouseDown(event);
          }}
          $edgeInset={edgeInset}
          {...containerProps}
        >
          {children({ clickItem })}
        </ScrollContainer>
      )}
    </DragScroller>
  );
};

const DayCell = styled(Box)<{ $isSelected?: boolean }>`
  flex: 0 0 66px;
  width: 66px;
  height: 64px;
  border-radius: 8px;
  border: 1px solid
    ${({ theme, $isSelected }) =>
      $isSelected ? theme.colors.primary : 'transparent'};
  background: ${({ theme, $isSelected }) =>
    $isSelected ? theme.colors.grays[0] : 'transparent'};
  padding: 5px 4px 8px;
  box-sizing: border-box;
  cursor: pointer;
`;

const DayLabel = styled(Box)`
  font-size: 12px;
  font-weight: 700;
  line-height: 1.2;
  color: ${({ theme }) => theme.colors.black};
  margin-top: 2px;
  white-space: nowrap;
`;

const RiskBars = styled(Box)`
  display: flex;
  gap: 4px;
  width: 100%;
  margin-top: 4px;
`;

const RiskBar = styled(Box)<{ $color: string }>`
  flex: 1 1 0;
  height: 4px;
  border-radius: 2px;
  background: ${({ $color }) => $color};
`;

const LoadingCell = styled(Box)`
  flex: 0 0 66px;
  width: 66px;
  height: 64px;
  border-radius: 8px;
`;

const getRiskBarColor = (
  riskLevel: WeatherForecastRiskLevel,
  colors: {
    success: string;
    warning: string;
    danger: string;
    lightgrey: string;
  },
) => {
  switch (riskLevel) {
    case 'danger':
      return colors.danger;
    case 'warning':
      return colors.warning;
    case 'caution':
      return colors.success;
    default:
      return colors.lightgrey;
  }
};

export interface DayStripProps {
  forecastDays: WeatherForecastDay[];
  selectedDate?: string;
  onSelectDay?: WeatherForecastDaySelectHandler;
  isLoading?: boolean;
  error?: WeatherForecastOutlookError | null;
  /** Tighter horizontal inset to match compact forecast header padding. */
  compact?: boolean;
  showExtendedForecastCta?: boolean;
  loadingDayCount?: number;
}

const LoadingDayStrip: FC<{ edgeInset: number; dayCount: number }> = ({
  edgeInset,
  dayCount,
}) => (
  <DragScrollStrip
    aria-busy
    data-testid="weather-forecast-day-strip-loading"
    edgeInset={edgeInset}
  >
    {() =>
      Array.from({ length: dayCount }, (_, index) => (
        <LoadingCell key={`loading-day-${index}`} px={1} py={2}>
          <Shimmer on>
            <Box minHeight="24px" mb={1} />
          </Shimmer>
          <Shimmer on>
            <Box minHeight="12px" mb={2} />
          </Shimmer>
          <Shimmer on>
            <Box minHeight="6px" />
          </Shimmer>
        </LoadingCell>
      ))
    }
  </DragScrollStrip>
);

const DayStrip: FC<DayStripProps> = ({
  forecastDays,
  selectedDate,
  onSelectDay,
  isLoading = false,
  error = null,
  compact = false,
  showExtendedForecastCta = false,
  loadingDayCount = FREE_FORECAST_DAYS,
}) => {
  const theme = useTheme();
  const edgeInset = compact ? theme.space[2] : theme.space[3];
  const scrollContainerRef = useRef<HTMLElement | null>(null);
  const dayCellRefs = useRef<Record<string, HTMLElement | null>>({});

  const scrollSelectedDayIntoView = useCallback(
    (date: string | undefined) => {
      if (!date || !scrollContainerRef.current) {
        return;
      }

      const selectedDayElement = dayCellRefs.current[date];
      if (!selectedDayElement) {
        return;
      }

      const selectedDayIndex = forecastDays.findIndex(
        (day) => day.date === date,
      );
      const previousDayDate =
        date === EXTENDED_FORECAST_CTA_DAY
          ? forecastDays[forecastDays.length - 1]?.date
          : forecastDays[selectedDayIndex - 1]?.date;
      const nextDayDate =
        date === EXTENDED_FORECAST_CTA_DAY
          ? undefined
          : forecastDays[selectedDayIndex + 1]?.date;
      const previousDayElement = previousDayDate
        ? dayCellRefs.current[previousDayDate]
        : null;
      const nextDayElement = nextDayDate
        ? dayCellRefs.current[nextDayDate]
        : null;

      scrollDayStripToSelectedDay({
        container: scrollContainerRef.current,
        selectedDayElement,
        previousDayElement,
        nextDayElement,
      });
    },
    [forecastDays],
  );

  useEffect(() => {
    scrollSelectedDayIntoView(selectedDate);
  }, [scrollSelectedDayIntoView, selectedDate]);

  if (isLoading) {
    return <LoadingDayStrip edgeInset={edgeInset} dayCount={loadingDayCount} />;
  }

  if (error) {
    return (
      <MessageBlock type={error.type} size="small">
        {error.message}
      </MessageBlock>
    );
  }

  if (!forecastDays.length) {
    return null;
  }

  const handleStopScrolling = () => {
    captureEvent({
      type: 'Weather forecast: day strip scrolled',
      payload: {},
      sendToAmplitude: true,
    });
  };

  return (
    <DragScrollStrip
      data-testid="weather-forecast-day-strip"
      edgeInset={edgeInset}
      scrollContainerRef={scrollContainerRef}
      onStopScrolling={handleStopScrolling}
    >
      {({ clickItem }) => (
        <>
          {forecastDays.map((forecastDay) => {
            const isSelected = forecastDay.date === selectedDate;

            return (
              <DayCell
                key={forecastDay.date}
                ref={(element: HTMLElement | null) => {
                  dayCellRefs.current[forecastDay.date] = element;
                }}
                $isSelected={isSelected}
                display="flex"
                flexDirection="column"
                alignItems="center"
                role="button"
                tabIndex={0}
                aria-pressed={isSelected}
                data-testid={
                  forecastDay.isToday
                    ? 'weather-forecast-day-today'
                    : `weather-forecast-day-${forecastDay.dayLabel}`
                }
                data-selected={isSelected ? 'true' : 'false'}
                onClick={() => {
                  clickItem(() => {
                    onSelectDay?.(
                      forecastDay.date,
                      forecastDay.dayLabel,
                      'day strip',
                    );
                    scrollSelectedDayIntoView(forecastDay.date);
                  });
                }}
                onKeyDown={(event: KeyboardEvent) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    onSelectDay?.(
                      forecastDay.date,
                      forecastDay.dayLabel,
                      'day strip',
                    );
                    scrollSelectedDayIntoView(forecastDay.date);
                  }
                }}
              >
                {forecastDay.weatherIcon ? (
                  <Box display="flex" alignItems="center" minHeight="24px">
                    <ReactSvg
                      toDisplay={getWeatherPrecisIcon(forecastDay.weatherIcon)}
                      width={24}
                      height={24}
                    />
                  </Box>
                ) : (
                  <Box minHeight="24px" />
                )}

                <DayLabel textAlign="center">{forecastDay.dayLabel}</DayLabel>

                <RiskBars aria-hidden="true">
                  <RiskBar
                    $color={getRiskBarColor(
                      forecastDay.morningRiskLevel,
                      theme.colors,
                    )}
                    data-testid={`weather-forecast-day-${forecastDay.dayLabel}-morning`}
                  />
                  <RiskBar
                    $color={getRiskBarColor(
                      forecastDay.nightRiskLevel,
                      theme.colors,
                    )}
                    data-testid={`weather-forecast-day-${forecastDay.dayLabel}-night`}
                  />
                </RiskBars>
              </DayCell>
            );
          })}
          {showExtendedForecastCta ? (
            <Box
              ref={(element: HTMLElement | null) => {
                dayCellRefs.current[EXTENDED_FORECAST_CTA_DAY] = element;
              }}
            >
              <ExtendedForecastCtaChip onActivate={clickItem} />
            </Box>
          ) : null}
        </>
      )}
    </DragScrollStrip>
  );
};

export default DayStrip;
